import React, { useState, useEffect, useRef } from 'react';
import {
  Card, Box, CircularProgress, Divider, Typography,
  FormControl, InputLabel, Select as MuiSelect, MenuItem,
  Snackbar, Button as MuiButton,
} from '@mui/material';
import { QuizOutlined } from '@mui/icons-material';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import { useServicePlanApi } from '../services/servicePlanApi';
import { Notify } from 'notiflix';
import AnswerOptionsModal from './AnswerOptionsModal';
import usePermission from 'hooks/usePermission';
import NoPermissionNotice from 'components/NoPermissionNotice';

// ─────────────────────────────────────────────────────────
//  Constants
// ─────────────────────────────────────────────────────────
const ACCENT = '#667eea';

let _n = 0;
const uid     = () => `_new_${++_n}_${Math.random().toString(36).slice(2, 6)}`;
const isNewId = id  => typeof id === 'string' && id.startsWith('_new_');

const QTYPE_COLOR = {
  single:   '#3b82f6',
  multiple: '#22c55e',
  dropdown: '#f59e0b',
  text:     '#64748b',
  number:   '#a78bfa',
};

const API_LABEL = {
  single: 'single', multiple: 'multiple', dropdown: 'dropdown',
  text: 'text', number: 'number',
};

const MODAL_TO_API = {
  radio: 'single', checkbox: 'multiple', dropdown: 'dropdown',
  text: 'text', paragraph: 'text',
};

// ─────────────────────────────────────────────────────────
//  Primitives
// ─────────────────────────────────────────────────────────
function Btn({ children, onClick, primary, sm, danger, ghost, disabled, full }) {
  const bg = disabled ? '#f3f4f6'
    : danger   ? 'rgba(239,68,68,0.09)'
    : primary  ? ACCENT
    : ghost    ? 'transparent'
    : '#f3f4f6';
  const cl = disabled ? '#9ca3af'
    : danger   ? '#dc2626'
    : primary  ? '#fff'
    : ghost    ? ACCENT
    : '#374151';
  const bc = disabled ? '#e5e7eb'
    : danger   ? '#fca5a5'
    : (primary || ghost) ? ACCENT
    : '#e5e7eb';
  return (
    <button onClick={disabled ? undefined : onClick} style={{
      background: bg, color: cl, border: `1px solid ${bc}`, borderRadius: 7,
      padding: sm ? '4px 11px' : '8px 18px', fontSize: sm ? 11 : 13,
      fontWeight: 500, cursor: disabled ? 'default' : 'pointer',
      fontFamily: 'inherit', opacity: disabled ? 0.5 : 1,
      width: full ? '100%' : 'auto', whiteSpace: 'nowrap', transition: 'opacity 0.15s',
    }}>
      {children}
    </button>
  );
}

function Field({ label, children, hint }) {
  return (
    <div>
      {label && <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.07em', fontWeight: 600 }}>{label}</div>}
      {children}
      {hint && <div style={{ color: '#9ca3af', fontSize: 11, marginTop: 4 }}>{hint}</div>}
    </div>
  );
}

function Inp({ value, onChange, placeholder, style: st }) {
  return (
    <input value={value} onChange={onChange} placeholder={placeholder} style={{
      background: '#f9fafb', border: '1px solid #d1d5db', borderRadius: 7,
      padding: '8px 12px', color: '#111827', fontFamily: 'inherit', fontSize: 13,
      width: '100%', outline: 'none', boxSizing: 'border-box', ...st,
    }} />
  );
}

function Sel({ value, onChange, options, disabled, style: st }) {
  return (
    <select value={value} onChange={onChange} disabled={disabled} style={{
      background: '#f9fafb', border: '1px solid #d1d5db', borderRadius: 7,
      padding: '8px 10px', color: value ? '#111827' : '#9ca3af',
      fontFamily: 'inherit', fontSize: 13, width: '100%', outline: 'none', ...st,
    }}>
      {options.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
    </select>
  );
}

function Toggle({ checked, onChange, label, hint }) {
  return (
    <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer' }}>
      <div
        onClick={() => onChange(!checked)}
        style={{
          width: 36, height: 20, borderRadius: 10, flexShrink: 0, marginTop: 2,
          background: checked ? ACCENT : '#d1d5db',
          position: 'relative', transition: 'background 0.2s', cursor: 'pointer',
        }}
      >
        <div style={{
          position: 'absolute', top: 3, left: checked ? 18 : 3, width: 14, height: 14,
          borderRadius: '50%', background: '#fff', transition: 'left 0.2s',
          boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
        }} />
      </div>
      <div>
        <div style={{ fontSize: 13, fontWeight: 500, color: '#111827', lineHeight: 1.3 }}>{label}</div>
        {hint && <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>{hint}</div>}
      </div>
    </label>
  );
}

function Drawer({ children, title, onClose, width = 640 }) {
  return (
    <div
      onClick={e => e.target === e.currentTarget && onClose()}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1300, padding: 20 }}
    >
      <div style={{
        background: '#fff', border: '1px solid #e5e7eb', borderRadius: 14,
        width: '100%', maxWidth: width, maxHeight: '90vh', overflowY: 'auto',
        padding: 28, boxSizing: 'border-box', boxShadow: '0 20px 60px rgba(0,0,0,0.18)',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 22 }}>
          <h3 style={{ margin: 0, color: '#111827', fontSize: 17, fontWeight: 700 }}>{title}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#6b7280', fontSize: 22, cursor: 'pointer', lineHeight: 1 }}>×</button>
        </div>
        {children}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────
//  QuestionModal  — includes Required, Active, AffectsPlan
// ─────────────────────────────────────────────────────────
function QuestionModal({ q, questionnaireId, allQuestions, onSave, onClose, isChild, isFirstQuestion, questionsApi, answerOptionsApi }) {
  const [text,               setText]               = useState(q?.text || '');
  const [helperText,         setHelperText]         = useState(q?.helperText || '');
  const initType = q ? (q.type === 'multiple' ? 'checkbox' : q.type === 'single' ? 'radio' : q.type) : 'radio';
  const [type,               setType]               = useState(initType);
  const [opts,               setOpts]               = useState(() => q ? q.opts.map(o => ({ ...o })) : []);
  const [isRequired,         setIsRequired]         = useState(q?.isRequired ?? true);
  const [isActive,           setIsActive]           = useState(q?.isActive ?? true);
  const [affectsPlan,        setAffectsPlan]        = useState(q?.affectsPlanSelection ?? true);
  const [saving,             setSaving]             = useState(false);

  const hasOpts = ['radio', 'checkbox', 'dropdown'].includes(type);

  const ancestorIds = (() => {
    if (!q) return new Set();
    const ancestors = new Set();
    const findParents = (targetId) => {
      Object.values(allQuestions).forEach(x => {
        if (x.opts?.some(o => o.nextId === targetId) && !ancestors.has(x.id)) {
          ancestors.add(x.id);
          findParents(x.id);
        }
      });
    };
    findParents(q.id);
    return ancestors;
  })();

  const nxtOpts = [
    { v: '', l: '— End of path (no follow-up) —' },
    ...Object.values(allQuestions)
      .filter(x => x.id !== q?.id && !ancestorIds.has(x.id))
      .map(x => ({ v: x.id, l: x.text.length > 55 ? x.text.slice(0, 55) + '…' : x.text })),
  ];

  const handleSave = async () => {
    if (!text.trim()) { Notify.failure('Question text is required'); return; }
    setSaving(true);
    try {
      const apiType  = MODAL_TO_API[type] || type;
      let   questionId = q?.id;

      const payload = {
        questionnaireId: q?.questionnaireId || questionnaireId,
        text:            text.trim(),
        type:            apiType,
        orderIndex:      q ? (q.orderIndex || 0) : (isFirstQuestion ? 1 : 2),
        helperText:      helperText.trim() || null,
        isActive,
        isRequired,
        affectsPlanSelection: affectsPlan,
      };

      if (q) {
        await questionsApi.update(q.id, payload);
      } else {
        const res = await questionsApi.create(payload);
        questionId = res.data.data?.id || res.data?.id;
      }

      // Sync answer options
      if (hasOpts && questionId) {
        const originalOpts = q?.opts || [];
        const originalIds  = new Set(originalOpts.filter(o => !isNewId(o.id)).map(o => o.id));
        const keptIds      = new Set(opts.filter(o => !isNewId(o.id)).map(o => o.id));

        // Delete removed options
        for (const oid of originalIds) {
          if (!keptIds.has(oid)) await answerOptionsApi.delete(oid).catch(() => {});
        }

        // Create new / update changed options
        for (let i = 0; i < opts.length; i++) {
          const opt = opts[i];
          if (isNewId(opt.id)) {
            await answerOptionsApi.create({
              questionId, answerText: opt.text,
              nextQuestionId: opt.nextId || null,
              isActive: true, featureIds: [], meta: null, orderIndex: i,
            });
          } else {
            const orig = originalOpts.find(o => o.id === opt.id);
            if (orig && (orig.text !== opt.text || orig.nextId !== opt.nextId)) {
              await answerOptionsApi.update(opt.id, {
                questionId, answerText: opt.text,
                nextQuestionId: opt.nextId || null,
                featureIds: orig.featureIds || [],
                isActive: orig.isActive ?? true,
                meta: orig.meta || null, orderIndex: i,
              });
            }
          }
        }
      }

      onSave(questionId);
    } catch (e) {
      Notify.failure(e?.response?.data?.message || 'Failed to save question');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer title={isChild ? 'New Follow-up Question' : q ? 'Edit Question' : 'New Question'} onClose={onClose}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

        <Field label="Question Text">
          <Inp value={text} onChange={e => setText(e.target.value)} placeholder="Type your question…" />
        </Field>

        <Field label="Helper Text (optional)">
          <Inp value={helperText} onChange={e => setHelperText(e.target.value)} placeholder="Small hint shown below the question" />
        </Field>

        <Field label="Answer Type">
          <Sel value={type} onChange={e => setType(e.target.value)} options={[
            { v: 'radio',     l: 'Radio — single choice' },
            { v: 'checkbox',  l: 'Checkbox — multiple' },
            { v: 'dropdown',  l: 'Dropdown' },
            { v: 'text',      l: 'Short Text' },
            { v: 'paragraph', l: 'Paragraph' },
          ]} />
        </Field>

        {/* Toggles — matching original CreateQuestionModal */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: '14px 16px', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8 }}>
          <Toggle
            checked={isRequired}
            onChange={setIsRequired}
            label="Required"
            hint="Users must answer this question"
          />
          <Toggle
            checked={affectsPlan}
            onChange={setAffectsPlan}
            label="Affects Plan Selection"
            hint="This question influences which plans are recommended"
          />
        </div>

        {hasOpts && (
          <Field label="Answer Options & Follow-up Questions">
            {opts.length === 0 && (
              <div style={{ color: '#9ca3af', fontSize: 12, textAlign: 'center', padding: 12, border: '1px dashed #e5e7eb', borderRadius: 7, marginBottom: 8 }}>
                No options yet
              </div>
            )}
            {opts.map((o, i) => (
              <div key={o.id} style={{ display: 'grid', gridTemplateColumns: 'auto 1fr 1fr auto', gap: 8, marginBottom: 8, alignItems: 'center' }}>
                <span style={{ color: '#9ca3af', fontSize: 12, width: 18, textAlign: 'center' }}>{i + 1}</span>
                <input
                  value={o.text}
                  onChange={e => setOpts(p => p.map(x => x.id === o.id ? { ...x, text: e.target.value } : x))}
                  placeholder="Option text"
                  style={{ background: '#f9fafb', border: '1px solid #d1d5db', borderRadius: 7, padding: '7px 10px', color: '#111827', fontFamily: 'inherit', fontSize: 13, outline: 'none' }}
                />
                <Sel
                  value={o.nextId || ''}
                  onChange={e => setOpts(p => p.map(x => x.id === o.id ? { ...x, nextId: e.target.value || null } : x))}
                  options={nxtOpts}
                />
                <button
                  onClick={() => setOpts(p => p.filter(x => x.id !== o.id))}
                  style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: 18 }}
                >×</button>
              </div>
            ))}
            <Btn sm ghost onClick={() => setOpts(p => [...p, { id: uid(), text: '', nextId: null }])}>+ Add Option</Btn>
            <div style={{ color: '#9ca3af', fontSize: 11, marginTop: 6 }}>
              To attach features to answer options, save the question then use the "Manage" button on the tree node.
            </div>
          </Field>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, paddingTop: 14, borderTop: '1px solid #f3f4f6' }}>
          <Btn ghost onClick={onClose}>Cancel</Btn>
          <Btn primary onClick={handleSave} disabled={saving}>{saving ? 'Saving…' : 'Save Question'}</Btn>
        </div>
      </div>
    </Drawer>
  );
}

// ─────────────────────────────────────────────────────────
//  DeleteConfirmModal
// ─────────────────────────────────────────────────────────
function DeleteConfirmModal({ data, onConfirm, onCancel }) {
  if (!data) return null;
  const { questionText, descendants } = data;
  const hasChildren = descendants.length > 0;

  return (
    <div
      onClick={e => e.target === e.currentTarget && onCancel()}
      style={{
        position: 'fixed', inset: 0, zIndex: 1400,
        background: 'rgba(0,0,0,0.45)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 20,
      }}
    >
      <div style={{
        background: '#fff', borderRadius: 16, width: '100%', maxWidth: 460,
        boxShadow: '0 24px 64px rgba(0,0,0,0.18)',
        overflow: 'hidden',
        animation: 'fadeInScale 0.18s ease',
      }}>
        {/* Header */}
        <div style={{
          background: hasChildren ? '#fff7ed' : '#fef2f2',
          borderBottom: `1px solid ${hasChildren ? '#fed7aa' : '#fecaca'}`,
          padding: '20px 24px',
          display: 'flex', alignItems: 'center', gap: 12,
        }}>
          <div style={{
            width: 40, height: 40, borderRadius: '50%', flexShrink: 0,
            background: hasChildren ? '#f97316' : '#ef4444',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 20,
          }}>
            ⚠
          </div>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#111827' }}>
              {hasChildren ? 'Delete Question & Children' : 'Delete Question'}
            </div>
            <div style={{ fontSize: 13, color: '#6b7280', marginTop: 2 }}>
              This action cannot be undone
            </div>
          </div>
        </div>

        {/* Body */}
        <div style={{ padding: '20px 24px' }}>
          <p style={{ margin: '0 0 14px', fontSize: 14, color: '#374151', lineHeight: 1.6 }}>
            Are you sure you want to delete{' '}
            <strong style={{ color: '#111827' }}>"{questionText}"</strong>?
          </p>

          {hasChildren && (
            <div style={{
              background: '#fff7ed', border: '1px solid #fed7aa',
              borderRadius: 10, padding: '14px 16px', marginBottom: 14,
            }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#c2410c', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
                <span>⚠</span>
                {descendants.length} child question{descendants.length > 1 ? 's' : ''} will also be deleted:
              </div>
              <div style={{
                maxHeight: 160, overflowY: 'auto',
                display: 'flex', flexDirection: 'column', gap: 6,
                '&::-webkit-scrollbar': { width: 4 },
              }}>
                {descendants.map((d, i) => (
                  <div key={d.id} style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    padding: '6px 10px',
                    background: '#fff', border: '1px solid #fed7aa',
                    borderRadius: 6, fontSize: 13, color: '#374151',
                  }}>
                    <span style={{ fontSize: 11, fontWeight: 700, color: '#f97316', minWidth: 18, textAlign: 'center' }}>
                      {i + 1}
                    </span>
                    <span style={{ fontWeight: 500 }}>{d.text}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div style={{
            fontSize: 13, color: '#dc2626', fontWeight: 500,
            background: 'rgba(220,38,38,0.06)', border: '1px solid rgba(220,38,38,0.2)',
            borderRadius: 7, padding: '8px 12px',
          }}>
            🗑 Once deleted, this data cannot be recovered.
          </div>
        </div>

        {/* Actions */}
        <div style={{
          padding: '16px 24px', borderTop: '1px solid #f3f4f6',
          display: 'flex', justifyContent: 'flex-end', gap: 10,
        }}>
          <button onClick={onCancel} style={{
            fontSize: 14, fontWeight: 500, color: '#374151',
            background: '#f9fafb', border: '1px solid #e5e7eb',
            borderRadius: 8, padding: '9px 20px', cursor: 'pointer', fontFamily: 'inherit',
          }}>
            Cancel
          </button>
          <button onClick={onConfirm} style={{
            fontSize: 14, fontWeight: 600, color: '#fff',
            background: '#dc2626', border: '1px solid #dc2626',
            borderRadius: 8, padding: '9px 22px', cursor: 'pointer', fontFamily: 'inherit',
            boxShadow: '0 2px 8px rgba(220,38,38,0.3)',
          }}>
            {hasChildren ? `Delete All (${descendants.length + 1})` : 'Delete'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────
//  QTree  — PackageFlow visual, deeper colors
// ─────────────────────────────────────────────────────────
function QTree({ qid, questions, depth, onEdit, onDelete, onAddChild, onManageAnswers, onAssignFollowup, visited }) {
  const [open, setOpen] = useState(depth < 2);
  // Drag-to-reuse a follow-up question across sibling options.
  // dragRef holds the dragged question id (synchronous — no setState timing/stale-closure
  // issues mid-drag). It is LOCAL to this QTree node, so drops are confined to options of
  // the SAME question (same level — never an ancestor or descendant question).
  const dragRef = useRef(null);
  const [dragActive,    setDragActive]    = useState(false); // toggles drop-zone visuals
  const [dragOverOptId, setDragOverOptId] = useState(null);

  if (!qid || !questions[qid]) return null;
  if (visited.has(qid)) {
    return (
      <div style={{ color: '#9ca3af', fontSize: 13, padding: '6px 14px', fontStyle: 'italic' }}>
        ↩ loops back to: "{questions[qid]?.text?.slice(0, 40)}"
      </div>
    );
  }

  const vis2 = new Set(visited).add(qid);
  const q    = questions[qid];
  const qc   = QTYPE_COLOR[q.type] || '#64748b';

  // Deeper node styling
  const nodeBg     = depth === 0 ? 'rgba(102,126,234,0.1)' : 'rgba(0,0,0,0.035)';
  const nodeBorder = depth === 0 ? `rgba(102,126,234,0.45)` : '#c7cdd8';
  const leftBorder = q.isRoot ? ACCENT : qc;

  return (
    <div style={{ marginBottom: 6 }}>
      {/* ── Question node ── */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10,
        background: nodeBg,
        border: `1px solid ${nodeBorder}`,
        borderLeft: `3px solid ${leftBorder}`,
        borderRadius: 9, padding: '11px 16px',
      }}>
        {/* expand toggle */}
        <button
          onClick={() => setOpen(v => !v)}
          style={{
            background: 'none', border: 'none', color: '#6b7280',
            cursor: 'pointer', fontSize: 14, padding: 0, flexShrink: 0,
            transform: open ? 'rotate(90deg)' : 'none', transition: 'transform 0.2s',
          }}
        >
          {q.opts.length > 0 ? '▶' : '·'}
        </button>

        {/* type badge */}
        <span style={{
          fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em',
          color: qc, background: `${qc}22`, padding: '4px 10px', borderRadius: 4, flexShrink: 0,
          border: `1px solid ${qc}44`,
        }}>
          {API_LABEL[q.type] || q.type}
        </span>

        {/* question text */}
        <span style={{ flex: 1, color: '#111827', fontSize: 15, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {q.text}
        </span>

        {/* root badge */}
        {q.isRoot && (
          <span style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', color: ACCENT, background: `${ACCENT}18`, border: `1px solid ${ACCENT}44`, padding: '4px 10px', borderRadius: 4, flexShrink: 0 }}>
            Root
          </span>
        )}

        {/* opts count */}
        <span style={{ color: '#6b7280', fontSize: 14, flexShrink: 0, fontWeight: 600 }}>{q.opts.length} opts</span>

        {/* required badge */}
        {q.isRequired && (
          <span style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', color: '#dc2626', background: 'rgba(220,38,38,0.1)', border: '1px solid rgba(220,38,38,0.25)', padding: '4px 10px', borderRadius: 4, flexShrink: 0 }}>
            Required
          </span>
        )}

        {/* Manage button */}
        <button
          onClick={() => onManageAnswers(qid)}
          style={{
            fontSize: 13, fontWeight: 600, color: '#16a34a', background: 'rgba(22,163,74,0.08)',
            border: '1px solid rgba(22,163,74,0.3)', borderRadius: 5,
            padding: '5px 13px', cursor: 'pointer', fontFamily: 'inherit', flexShrink: 0,
          }}
        >
          Manage
        </button>

        <Btn sm ghost onClick={() => onEdit(qid)}>Edit</Btn>
        <Btn sm danger onClick={() => onDelete(qid)}>×</Btn>
      </div>

      {/* ── Answer options + recursive children ── */}
      {open && q.opts.length > 0 && (
        <div style={{ marginLeft: 28, marginTop: 4 }}>
          {q.opts.map(opt => {
            const childQ      = opt.nextId && questions[opt.nextId];
            const hasFollowup = Boolean(childQ);           // valid follow-up → can be dragged/copied
            const canDrop     = !hasFollowup;              // no follow-up yet → can receive a copy
            const isDropTarget = canDrop && dragOverOptId === opt.id;
            return (
              <div key={opt.id} style={{ position: 'relative' }}>
                {/* vertical tree line */}
                <div style={{ position: 'absolute', left: -20, top: 0, bottom: childQ ? 0 : '50%', width: 1, background: '#94a3b8' }} />
                {/* horizontal tree line */}
                <div style={{ position: 'absolute', left: -20, top: 16, width: 20, height: 1, background: '#94a3b8' }} />

                <div style={{ marginBottom: childQ ? 8 : 4, marginTop: 4 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: childQ ? 6 : 0 }}>
                    {/* dot */}
                    <div style={{ width: 7, height: 7, borderRadius: '50%', background: '#94a3b8', flexShrink: 0 }} />

                    {/* answer text pill — draggable when it carries a follow-up, so it can be reused on sibling options */}
                    <span
                      draggable={hasFollowup}
                      onDragStart={hasFollowup ? (e) => {
                        e.dataTransfer.effectAllowed = 'copy';
                        try { e.dataTransfer.setData('text/plain', String(opt.nextId)); } catch { /* noop */ }
                        dragRef.current = opt.nextId;
                        setDragActive(true);
                      } : undefined}
                      onDragEnd={hasFollowup ? () => { dragRef.current = null; setDragActive(false); setDragOverOptId(null); } : undefined}
                      title={hasFollowup ? 'Drag onto another option of this question to copy this follow-up (independent copy)' : undefined}
                      style={{
                        color: '#374151', fontSize: 14, fontWeight: 500,
                        background: '#f1f5f9', border: '1px solid #cbd5e1',
                        padding: '5px 14px', borderRadius: 5,
                        display: 'inline-flex', alignItems: 'center', gap: 7,
                        cursor: hasFollowup ? 'grab' : 'default',
                      }}
                    >
                      {hasFollowup && <span style={{ color: '#94a3b8', fontSize: 13, lineHeight: 1 }}>⠿</span>}
                      {opt.text}
                    </span>

                    {hasFollowup ? (
                      <span style={{ color: '#64748b', fontSize: 14 }}>→</span>
                    ) : (
                      <button
                        onClick={() => onAddChild(qid, opt.id)}
                        onDragEnter={(e) => { if (dragRef.current) { e.preventDefault(); setDragOverOptId(opt.id); } }}
                        onDragOver={(e) => { if (dragRef.current) { e.preventDefault(); e.dataTransfer.dropEffect = 'copy'; setDragOverOptId(opt.id); } }}
                        onDragLeave={() => setDragOverOptId(prev => (prev === opt.id ? null : prev))}
                        onDrop={(e) => {
                          if (!dragRef.current) return;
                          e.preventDefault();
                          const nextId = dragRef.current;
                          dragRef.current = null;
                          setDragActive(false);
                          setDragOverOptId(null);
                          onAssignFollowup(qid, opt.id, nextId);
                        }}
                        style={{
                          fontSize: 13, fontWeight: 600,
                          color: '#15803d',
                          background: isDropTarget ? 'rgba(21,128,61,0.22)' : 'rgba(21,128,61,0.08)',
                          border: isDropTarget ? '1px solid #15803d' : '1px dashed rgba(21,128,61,0.45)',
                          borderRadius: 4, padding: '5px 14px', cursor: 'pointer', fontFamily: 'inherit',
                          transition: 'background 0.12s, border-color 0.12s',
                        }}
                      >
                        {dragActive ? '↳ Drop to copy follow-up' : '+ Follow-up Question'}
                      </button>
                    )}

                  </div>

                  {/* Recursive child question */}
                  {opt.nextId && (
                    <div style={{ marginLeft: 20 }}>
                      <QTree
                        qid={opt.nextId} questions={questions} depth={depth + 1}
                        onEdit={onEdit} onDelete={onDelete} onAddChild={onAddChild}
                        onManageAnswers={onManageAnswers} onAssignFollowup={onAssignFollowup} visited={vis2}
                      />
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────
//  ImportQuestionModal — copy a question (+ branch) from another page
// ─────────────────────────────────────────────────────────
function ImportQuestionModal({ currentQuestionnaireId, questionnaires, currentQuestions, fetchQuestionsMap, onImport, onClose }) {
  const others = (questionnaires || []).filter(q => q.id !== currentQuestionnaireId);

  // Does the CURRENT page already have questions? If so, the user must decide how the
  // copied question integrates: attach it under an existing answer, or replace everything.
  const currentCount = Object.keys(currentQuestions || {}).length;
  const hasExisting  = currentCount > 0;

  // Available attach points = answer options on the current page that have NO follow-up yet
  // (a leaf option). The imported question becomes that option's follow-up ("start from").
  const attachPoints = [];
  Object.values(currentQuestions || {}).forEach(q => {
    (q.opts || []).forEach(o => {
      if (!o.nextId) attachPoints.push({ parentQid: q.id, parentText: q.text, optId: o.id, optText: o.text });
    });
  });

  const [srcId,        setSrcId]        = useState('');
  const [srcMap,       setSrcMap]       = useState(null);
  const [loading,      setLoading]      = useState(false);
  const [selectedQid,  setSelectedQid]  = useState('');
  const [importing,    setImporting]    = useState(false);
  // Integration mode (only relevant when the current page already has questions).
  const [mode,         setMode]         = useState(attachPoints.length > 0 ? 'attach' : 'replace');
  const [attachOptId,  setAttachOptId]  = useState('');

  const loadSource = async (id) => {
    setSrcId(id);
    setSelectedQid('');
    setSrcMap(null);
    if (!id) return;
    setLoading(true);
    try {
      setSrcMap(await fetchQuestionsMap(id));
    } catch {
      Notify.failure('Failed to load that questionnaire');
    } finally {
      setLoading(false);
    }
  };

  // Count descendant questions reachable from a question (the branch that comes along).
  const countDescendants = (qid) => {
    if (!srcMap) return 0;
    const visited = new Set();
    const queue = [...(srcMap[qid]?.opts || []).map(o => o.nextId).filter(Boolean)];
    while (queue.length) {
      const id = queue.shift();
      if (!id || visited.has(id) || !srcMap[id]) continue;
      visited.add(id);
      (srcMap[id].opts || []).forEach(o => { if (o.nextId) queue.push(o.nextId); });
    }
    return visited.size;
  };

  const list = srcMap
    ? Object.values(srcMap).sort((a, b) => (a.orderIndex || 0) - (b.orderIndex || 0))
    : [];

  // Build the integration descriptor passed to the import handler.
  const integration = !hasExisting
    ? { mode: 'root' }                                   // empty page → becomes the first/root question
    : mode === 'replace'
      ? { mode: 'replace' }
      : (() => {
          const ap = attachPoints.find(a => a.optId === attachOptId);
          return ap ? { mode: 'attach', parentQid: ap.parentQid, targetOptId: ap.optId } : null;
        })();

  const canCopy = Boolean(selectedQid && srcMap && integration);

  const handleCopy = async () => {
    if (!canCopy) return;
    setImporting(true);
    try {
      await onImport(selectedQid, srcMap, integration);
    } finally {
      setImporting(false);
    }
  };

  return (
    <Drawer title="Copy a question from another page" onClose={onClose} width={680}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <Field label="Source page / questionnaire" hint="The page you want to copy a question from.">
          <FormControl fullWidth size="small">
            <InputLabel id="import-src-label">Select a questionnaire</InputLabel>
            <MuiSelect
              labelId="import-src-label"
              value={srcId}
              label="Select a questionnaire"
              onChange={e => loadSource(e.target.value)}
              sx={{ '& .MuiSelect-select': { py: 1.1 } }}
              MenuProps={{ PaperProps: { sx: { maxHeight: 340, mt: 0.5 } } }}
            >
              {others.map(q => (
                <MenuItem key={q.id} value={q.id}>
                  <Box>
                    <Typography sx={{ fontSize: 14, fontWeight: 600, lineHeight: 1.3 }}>{q.name}</Typography>
                    {q.page?.route && (
                      <Typography sx={{ fontSize: 12, color: 'text.secondary', lineHeight: 1.3 }}>
                        {q.page.route}
                      </Typography>
                    )}
                  </Box>
                </MenuItem>
              ))}
            </MuiSelect>
          </FormControl>
        </Field>

        {loading && (
          <Box display="flex" alignItems="center" gap={1.5} py={2}>
            <CircularProgress size={18} sx={{ color: ACCENT }} />
            <span style={{ fontSize: 13, color: '#6b7280' }}>Loading questions…</span>
          </Box>
        )}

        {!loading && srcMap && list.length === 0 && (
          <div style={{ color: '#9ca3af', fontSize: 13, textAlign: 'center', padding: 14, border: '1px dashed #e5e7eb', borderRadius: 8 }}>
            This questionnaire has no questions.
          </div>
        )}

        {!loading && list.length > 0 && (
          <Field label="Pick a question to copy" hint="Its entire follow-up branch is copied too. The copy is fully independent of the original.">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 340, overflowY: 'auto' }}>
              {list.map(q => {
                const sel  = selectedQid === q.id;
                const desc = countDescendants(q.id);
                const qc   = QTYPE_COLOR[q.type] || '#64748b';
                return (
                  <div
                    key={q.id}
                    onClick={() => setSelectedQid(q.id)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer',
                      padding: '10px 12px', borderRadius: 8,
                      border: sel ? `1px solid ${ACCENT}` : '1px solid #e5e7eb',
                      background: sel ? 'rgba(102,126,234,0.07)' : '#fff',
                      transition: 'border 0.12s, background 0.12s',
                    }}
                  >
                    <span style={{
                      width: 16, height: 16, borderRadius: '50%', flexShrink: 0, boxSizing: 'border-box',
                      border: sel ? `5px solid ${ACCENT}` : '2px solid #cbd5e1', transition: 'border 0.15s',
                    }} />
                    <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: qc, background: `${qc}1f`, border: `1px solid ${qc}44`, padding: '2px 8px', borderRadius: 4, flexShrink: 0 }}>
                      {API_LABEL[q.type] || q.type}
                    </span>
                    <span style={{ flex: 1, fontSize: 14, fontWeight: 600, color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {q.text}
                    </span>
                    {q.isRoot && <span style={{ fontSize: 10, fontWeight: 700, color: ACCENT, flexShrink: 0 }}>ROOT</span>}
                    <span style={{ fontSize: 12, color: '#6b7280', flexShrink: 0 }}>{q.opts.length} opts</span>
                    {desc > 0 && <span style={{ fontSize: 12, color: '#15803d', flexShrink: 0 }}>+{desc} follow-up{desc > 1 ? 's' : ''}</span>}
                  </div>
                );
              })}
            </div>
          </Field>
        )}

        {/* Integration step — only when the current page already has questions */}
        {!loading && selectedQid && hasExisting && (
          <Field label="How should it be added to this page?" hint="This page already has questions — choose where the copied question goes.">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {/* Mode: Attach */}
              <div
                onClick={() => attachPoints.length > 0 && setMode('attach')}
                style={{
                  border: mode === 'attach' ? `1px solid ${ACCENT}` : '1px solid #e5e7eb',
                  background: mode === 'attach' ? 'rgba(102,126,234,0.06)' : '#fff',
                  borderRadius: 10, padding: '12px 14px',
                  cursor: attachPoints.length > 0 ? 'pointer' : 'not-allowed',
                  opacity: attachPoints.length > 0 ? 1 : 0.55,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ width: 16, height: 16, borderRadius: '50%', flexShrink: 0, boxSizing: 'border-box', border: mode === 'attach' ? `5px solid ${ACCENT}` : '2px solid #cbd5e1' }} />
                  <span style={{ fontSize: 14, fontWeight: 600, color: '#111827' }}>Attach to an existing answer</span>
                </div>
                <div style={{ fontSize: 12, color: '#6b7280', marginLeft: 26, marginTop: 2 }}>
                  {attachPoints.length > 0
                    ? 'Pick which answer option this copied question should start from.'
                    : 'No free answer options — every option already has a follow-up. Use Replace instead.'}
                </div>

                {mode === 'attach' && attachPoints.length > 0 && (
                  <div style={{ marginLeft: 26, marginTop: 10 }}>
                    <FormControl fullWidth size="small">
                      <InputLabel id="import-attach-label">Start from which option</InputLabel>
                      <MuiSelect
                        labelId="import-attach-label"
                        value={attachOptId}
                        label="Start from which option"
                        onChange={e => setAttachOptId(e.target.value)}
                        onClick={e => e.stopPropagation()}
                        MenuProps={{ PaperProps: { sx: { maxHeight: 320 } } }}
                      >
                        {attachPoints.map(ap => (
                          <MenuItem key={ap.optId} value={ap.optId}>
                            <Box>
                              <Typography sx={{ fontSize: 14, fontWeight: 600, lineHeight: 1.3 }}>{ap.optText}</Typography>
                              <Typography sx={{ fontSize: 12, color: 'text.secondary', lineHeight: 1.3 }}>
                                in: {ap.parentText}
                              </Typography>
                            </Box>
                          </MenuItem>
                        ))}
                      </MuiSelect>
                    </FormControl>
                  </div>
                )}
              </div>

              {/* Mode: Replace */}
              <div
                onClick={() => setMode('replace')}
                style={{
                  border: mode === 'replace' ? '1px solid #dc2626' : '1px solid #e5e7eb',
                  background: mode === 'replace' ? 'rgba(220,38,38,0.05)' : '#fff',
                  borderRadius: 10, padding: '12px 14px', cursor: 'pointer',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ width: 16, height: 16, borderRadius: '50%', flexShrink: 0, boxSizing: 'border-box', border: mode === 'replace' ? '5px solid #dc2626' : '2px solid #cbd5e1' }} />
                  <span style={{ fontSize: 14, fontWeight: 600, color: '#111827' }}>Replace everything on this page</span>
                </div>
                <div style={{ fontSize: 12, color: '#6b7280', marginLeft: 26, marginTop: 2 }}>
                  Deletes the current {currentCount} question{currentCount > 1 ? 's' : ''} on this page, then adds the copied question as the new starting question.
                </div>
                {mode === 'replace' && (
                  <div style={{ marginLeft: 26, marginTop: 10, fontSize: 12, color: '#dc2626', fontWeight: 500, background: 'rgba(220,38,38,0.06)', border: '1px solid rgba(220,38,38,0.2)', borderRadius: 7, padding: '8px 12px' }}>
                    ⚠ This permanently deletes all existing questions on this page. This cannot be undone.
                  </div>
                )}
              </div>
            </div>
          </Field>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, paddingTop: 14, borderTop: '1px solid #f3f4f6' }}>
          <Btn ghost onClick={onClose}>Cancel</Btn>
          <Btn
            primary={!(hasExisting && mode === 'replace')}
            danger={hasExisting && mode === 'replace'}
            onClick={handleCopy}
            disabled={!canCopy || importing}
          >
            {importing
              ? (mode === 'replace' ? 'Replacing…' : 'Copying…')
              : hasExisting
                ? (mode === 'replace' ? 'Delete & replace' : 'Attach to this page')
                : 'Copy to this page'}
          </Btn>
        </div>
      </div>
    </Drawer>
  );
}

// ─────────────────────────────────────────────────────────
//  QuestionsContent
// ─────────────────────────────────────────────────────────
function QuestionsContent({ questionnaireId, questionnaires, questionsApi, answerOptionsApi }) {
  const canCreate = usePermission('qa-center', 'create');
  const [questions,     setQuestions]     = useState({});
  const [loading,       setLoading]       = useState(false);
  const [modal,         setModal]         = useState(null);
  const [answerModal,   setAnswerModal]   = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [importModal,   setImportModal]   = useState(false);
  // Undo for the last "add" action (new question / follow-up / drag-copy / import).
  // null | { label, createdRootId, parentOptRestore: { parentQid, optId, prevNextId } | null, busy }
  const [undo,          setUndo]          = useState(null);

  useEffect(() => { if (questionnaireId) loadAll(); }, [questionnaireId]);

  // Fetch a questionnaire's questions + options and build the in-memory tree map.
  // Pure (no state writes) so it can be reused to read a DIFFERENT questionnaire
  // when importing/copying a question from another page.
  const fetchQuestionsMap = async (qnaireId) => {
    const qRes = await questionsApi.getAll(qnaireId);
    const qs   = qRes.data.data || qRes.data || [];

    const results = await Promise.all(
      qs.map(q =>
        answerOptionsApi.getByQuestion(q.id)
          .then(r => ({ qid: q.id, opts: r.data.data || r.data || [] }))
          .catch(() => ({ qid: q.id, opts: [] }))
      )
    );

    const optsMap = {};
    results.forEach(({ qid, opts }) => { optsMap[qid] = opts; });

    // Root = a question no answer points to (fallback: first by orderIndex)
    const allNextIds = new Set(
      results.flatMap(({ opts }) => opts.map(o => o.nextQuestionId).filter(Boolean))
    );

    const map = {};
    qs.forEach(q => {
      map[q.id] = {
        id: q.id, questionnaireId: q.questionnaireId || qnaireId,
        type: q.type, text: q.text, helperText: q.helperText,
        orderIndex: q.orderIndex || 0, isActive: q.isActive,
        isRequired: q.isRequired, affectsPlanSelection: q.affectsPlanSelection,
        isRoot: !allNextIds.has(q.id),
        opts: (optsMap[q.id] || []).map(a => ({
          id: a.id, text: a.answerText, nextId: a.nextQuestionId || null,
          isActive: a.isActive, featureIds: a.answerFeatures?.map(f => f.featureId) || [],
          meta: a.meta, orderIndex: a.orderIndex || 0,
        })),
      };
    });
    return map;
  };

  const loadAll = async () => {
    setLoading(true);
    setUndo(null); // a full (re)load invalidates any pending single-action undo
    try {
      const map = await fetchQuestionsMap(questionnaireId);
      setQuestions(map);
    } catch {
      Notify.failure('Failed to load questions');
    } finally {
      setLoading(false);
    }
  };

  // Re-fetch the tree WITHOUT flipping the `loading` flag, so the view updates
  // in place (React diffs the tree) instead of flashing the full-screen spinner.
  // Used after in-tree mutations like drag-and-drop where a "reload" feels jarring.
  const refreshQuestions = async () => {
    try {
      const map = await fetchQuestionsMap(questionnaireId);
      setQuestions(map);
    } catch {
      Notify.failure('Failed to refresh questions');
    }
  };

  // BFS to collect all descendant question IDs of a given question
  const getDescendants = (rootQid) => {
    const result = [];
    const visited = new Set();
    const queue = [...(questions[rootQid]?.opts || []).map(o => o.nextId).filter(Boolean)];
    while (queue.length) {
      const id = queue.shift();
      if (!id || visited.has(id) || !questions[id]) continue;
      visited.add(id);
      result.push(id);
      (questions[id].opts || []).forEach(o => { if (o.nextId) queue.push(o.nextId); });
    }
    return result;
  };

  // Delete a question and its entire follow-up branch (children deleted first).
  // Used by undo to remove a just-created subtree. Reads descendants from the
  // current tree state (which still contains the subtree at undo time).
  const deleteSubtree = async (rootQid) => {
    if (!rootQid) return;
    const ids = [...getDescendants(rootQid).reverse(), rootQid];
    await Promise.all(ids.map(id => questionsApi.delete(id).catch(() => {})));
  };

  // Reverse the last "add" action: restore the parent option's previous follow-up
  // link (if any), then delete the newly-created question subtree.
  const runUndo = async () => {
    if (!undo || undo.busy) return;
    setUndo(u => (u ? { ...u, busy: true } : u));
    try {
      if (undo.parentOptRestore) {
        const { parentQid, optId, prevNextId } = undo.parentOptRestore;
        const opt = questions[parentQid]?.opts.find(o => o.id === optId);
        await answerOptionsApi.update(optId, {
          questionId:     parentQid,
          answerText:     opt?.text || '',
          nextQuestionId: prevNextId || null,
          featureIds:     opt?.featureIds || [],
          isActive:       opt?.isActive ?? true,
          meta:           opt?.meta || null,
          orderIndex:     opt?.orderIndex || 0,
        });
      }
      await deleteSubtree(undo.createdRootId);
      Notify.success('Undone');
      setUndo(null);
      await refreshQuestions();
    } catch (e) {
      Notify.failure(e?.response?.data?.message || 'Failed to undo');
      setUndo(u => (u ? { ...u, busy: false } : u));
    }
  };

  // Open the styled delete confirmation modal
  const handleDelete = (qid) => {
    const q = questions[qid];
    if (!q) return;
    const descendants = getDescendants(qid).map(id => ({ id, text: questions[id]?.text || id }));
    setDeleteConfirm({ qid, questionText: q.text, descendants });
  };

  // Called when user clicks "Delete" inside the modal
  const handleDeleteConfirmed = async () => {
    const { qid, descendants } = deleteConfirm;
    setDeleteConfirm(null);
    try {
      const allIds   = [...descendants.map(d => d.id).reverse(), qid];
      const deleted  = new Set(allIds);

      // Clear follow-up links that point at any question being deleted, so the parent
      // options reset to "no follow-up" instead of dangling as "→ [deleted]".
      const danglingOpts = [];
      Object.values(questions).forEach(q => {
        if (deleted.has(q.id)) return; // this question is going away anyway
        (q.opts || []).forEach(o => {
          if (o.nextId && deleted.has(o.nextId)) danglingOpts.push({ q, o });
        });
      });
      await Promise.all(danglingOpts.map(({ q, o }) =>
        answerOptionsApi.update(o.id, {
          questionId:     q.id,
          answerText:     o.text || '',
          nextQuestionId: null,
          featureIds:     o.featureIds || [],
          isActive:       o.isActive ?? true,
          meta:           o.meta || null,
          orderIndex:     o.orderIndex || 0,
        }).catch(() => {})
      ));

      await Promise.all(allIds.map(id => questionsApi.delete(id).catch(() => {})));
      Notify.success(
        descendants.length > 0
          ? `Question and ${descendants.length} child question${descendants.length > 1 ? 's' : ''} deleted`
          : 'Question deleted'
      );
      loadAll();
    } catch (e) {
      Notify.failure(e?.response?.data?.message || 'Failed to delete');
    }
  };

  // After QuestionModal saves — wire follow-up link then reload
  const handleModalSave = async (savedQid) => {
    const savedModal = modal; // capture before clearing
    setModal(null);

    let parentOptRestore = null;
    if (savedModal?.type === 'child' && savedModal.parentAnswerId && savedQid) {
      try {
        const parentOpt = questions[savedModal.parentQid]?.opts.find(o => o.id === savedModal.parentAnswerId);
        // Remember the option's previous follow-up so undo can restore it.
        parentOptRestore = {
          parentQid:  savedModal.parentQid,
          optId:      savedModal.parentAnswerId,
          prevNextId: parentOpt?.nextId || null,
        };
        await answerOptionsApi.update(savedModal.parentAnswerId, {
          questionId:     savedModal.parentQid,
          answerText:     parentOpt?.text || '',
          nextQuestionId: savedQid,
          featureIds:     parentOpt?.featureIds || [],
          isActive:       parentOpt?.isActive ?? true,
          meta:           parentOpt?.meta || null,
          orderIndex:     parentOpt?.orderIndex || 0,
        });
      } catch (e) {
        Notify.failure('Failed to link follow-up question to answer option');
      }
    }

    await refreshQuestions();

    // Offer undo only for freshly-created questions — never for edits (an edit's
    // savedQid is an existing question and must not be deletable via undo).
    if (savedQid && (savedModal?.type === 'new' || savedModal?.type === 'child')) {
      setUndo({
        label: savedModal.type === 'child' ? 'Follow-up question added' : 'Question added',
        createdRootId: savedQid,
        parentOptRestore,
      });
    }
  };

  // Sort questions by orderIndex; root = first with no incoming link
  const catQs = Object.values(questions).sort((a, b) => (a.orderIndex || 0) - (b.orderIndex || 0));
  const root  = catQs.find(q => q.isRoot) || catQs[0]; // fallback to first

  // BFS reachability from root
  const reachable = new Set();
  const walk = (qid, visited = new Set()) => {
    if (!qid || visited.has(qid) || !questions[qid]) return;
    visited.add(qid); reachable.add(qid);
    questions[qid].opts.forEach(o => { if (o.nextId) walk(o.nextId, visited); });
  };
  if (root) walk(root.id);
  const orphans = catQs.filter(q => !reachable.has(q.id));

  if (loading) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" minHeight={280} gap={1.5}>
        <CircularProgress size={22} sx={{ color: ACCENT }} />
        <MDTypography variant="body2" color="text.secondary">Loading question tree…</MDTypography>
      </Box>
    );
  }

  const handleAddChild = (parentQid, parentOptId) => {
    // parentOptId IS the answer option's id
    setModal({ type: 'child', parentQid, parentOptId, parentAnswerId: parentOptId });
  };

  // Deep-clone a follow-up question subtree into brand-new records and return the new
  // question id. Recurses through each option's follow-up so the whole branch is
  // duplicated — the copy shares NO records with the source, so editing one later
  // never affects the other. `pathIds` guards against cyclic links (a loop-back link
  // is simply dropped in the clone rather than recursed into forever).
  // srcMap defaults to the current questionnaire's questions (drag-to-copy within a page),
  // but can be a DIFFERENT questionnaire's map when importing from another page. The clone
  // is always created into the CURRENTLY selected questionnaire (`questionnaireId`).
  const cloneSubtree = async (srcQid, srcMap = questions, pathIds = new Set()) => {
    const src = srcMap[srcQid];
    if (!src) return null;

    const res = await questionsApi.create({
      questionnaireId:      questionnaireId,
      text:                 src.text,
      type:                 src.type,
      orderIndex:           src.orderIndex || 2,
      helperText:           src.helperText || null,
      isActive:             src.isActive ?? true,
      isRequired:           src.isRequired ?? true,
      affectsPlanSelection: src.affectsPlanSelection ?? true,
    });
    const newQid = res.data.data?.id || res.data?.id;
    if (!newQid) throw new Error('Failed to create cloned question');

    const nextPath = new Set(pathIds).add(srcQid);
    for (let i = 0; i < src.opts.length; i++) {
      const o = src.opts[i];
      let childNewId = null;
      if (o.nextId && srcMap[o.nextId] && !nextPath.has(o.nextId)) {
        childNewId = await cloneSubtree(o.nextId, srcMap, nextPath);
      }
      await answerOptionsApi.create({
        questionId:     newQid,
        answerText:     o.text,
        nextQuestionId: childNewId,
        isActive:       o.isActive ?? true,
        featureIds:     o.featureIds || [],
        meta:           o.meta || null,
        orderIndex:     o.orderIndex ?? i,
      });
    }
    return newQid;
  };

  // Copy an existing follow-up onto a sibling option (drag-and-drop in the tree).
  // Only ever called with options of the SAME question (enforced by QTree-local drag
  // state). The dragged follow-up is DEEP-COPIED so the target option gets its own
  // independent question — editing its name/options will not affect the original.
  const handleAssignFollowup = async (parentQid, targetOptId, srcNextId) => {
    const targetOpt = questions[parentQid]?.opts.find(o => o.id === targetOptId);
    if (!targetOpt || !srcNextId || targetOpt.nextId === srcNextId) return;
    try {
      const newQid = await cloneSubtree(srcNextId);
      if (!newQid) throw new Error('Failed to copy follow-up question');
      await answerOptionsApi.update(targetOptId, {
        questionId:     parentQid,
        answerText:     targetOpt.text || '',
        nextQuestionId: newQid,
        featureIds:     targetOpt.featureIds || [],
        isActive:       targetOpt.isActive ?? true,
        meta:           targetOpt.meta || null,
        orderIndex:     targetOpt.orderIndex || 0,
      });
      Notify.success('Follow-up question copied — this copy is independent');
      await refreshQuestions();
      setUndo({
        label: 'Follow-up question copied',
        createdRootId: newQid,
        parentOptRestore: { parentQid, optId: targetOptId, prevNextId: targetOpt.nextId || null },
      });
    } catch (e) {
      Notify.failure(e?.response?.data?.message || 'Failed to copy follow-up question');
    }
  };

  // Import a question (+ its whole follow-up branch) from ANOTHER questionnaire/page.
  // Deep-copies into the current questionnaire as independent records. `integration`
  // decides how it joins this page:
  //   { mode: 'root' }                              → empty page: becomes the first question
  //   { mode: 'attach', parentQid, targetOptId }    → links it under an existing answer option
  //   { mode: 'replace' }                           → deletes all current questions first, then root
  const handleImportQuestion = async (srcQid, srcMap, integration = { mode: 'root' }) => {
    try {
      if (integration.mode === 'replace') {
        const ids = Object.keys(questions);
        await Promise.all(ids.map(id => questionsApi.delete(id).catch(() => {})));
      }

      const newQid = await cloneSubtree(srcQid, srcMap);
      if (!newQid) throw new Error('Failed to import question');

      let parentOptRestore = null;
      if (integration.mode === 'attach' && integration.targetOptId) {
        const opt = questions[integration.parentQid]?.opts.find(o => o.id === integration.targetOptId);
        parentOptRestore = {
          parentQid:  integration.parentQid,
          optId:      integration.targetOptId,
          prevNextId: opt?.nextId || null,
        };
        await answerOptionsApi.update(integration.targetOptId, {
          questionId:     integration.parentQid,
          answerText:     opt?.text || '',
          nextQuestionId: newQid,
          featureIds:     opt?.featureIds || [],
          isActive:       opt?.isActive ?? true,
          meta:           opt?.meta || null,
          orderIndex:     opt?.orderIndex || 0,
        });
      }

      Notify.success(
        integration.mode === 'replace' ? 'Page replaced with the copied question'
        : integration.mode === 'attach' ? 'Question copied and attached to the selected option'
        : 'Question copied to this page — it is fully independent'
      );
      setImportModal(false);
      await refreshQuestions();

      // 'replace' deleted the originals — that can't be undone here, so only the
      // additive modes (root / attach) get an undo affordance.
      if (integration.mode !== 'replace') {
        setUndo({ label: 'Question imported', createdRootId: newQid, parentOptRestore });
      }
    } catch (e) {
      Notify.failure(e?.response?.data?.message || 'Failed to import question');
    }
  };

  const handleManageAnswers = (qid) => {
    const qObj = questions[qid];
    if (!qObj) return;
    // Pass the full reconstructed question object that AnswerOptionsModal expects
    const apiQuestion = {
      id:   qObj.id,
      text: qObj.text,
      type: qObj.type,
      questionnaireId: qObj.questionnaireId,
    };
    const allApiQs = catQs.map(x => ({
      id:   x.id,
      text: x.text,
      type: x.type,
    }));
    setAnswerModal({ question: apiQuestion, questions: allApiQs });
  };

  return (
    <>
      {/* Section header */}
      <Box display="flex" justifyContent="space-between" alignItems="flex-end" mb={2.5}>
        <Box>
          <MDTypography variant="h6" fontWeight="bold" sx={{ lineHeight: 1.3 }}>
            Question Tree{' '}
            <span style={{ color: '#9ca3af', fontSize: 13, fontWeight: 400 }}>({catQs.length})</span>
          </MDTypography>
          <MDTypography variant="body2" color="text.secondary">
            Visual nested flow — each answer branches to a follow-up question
          </MDTypography>
        </Box>
        <Box display="flex" gap={1}>
          {(questionnaires?.filter(qn => qn.id !== questionnaireId).length > 0) && (
            <Btn ghost onClick={() => setImportModal(true)}>⤓ Copy from another page</Btn>
          )}
          {catQs.length === 0 && canCreate && (
            <Btn primary onClick={() => setModal({ type: 'new' })}>+ Add Question</Btn>
          )}
        </Box>
      </Box>

      {/* Tree */}
      {catQs.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 7, border: '1.5px dashed', borderColor: 'divider', borderRadius: 3 }}>
          <QuizOutlined sx={{ fontSize: 44, color: 'text.disabled', mb: 1 }} />
          <MDTypography variant="body2" color="text.secondary">
            No questions yet — click "+ Add Question" to begin
          </MDTypography>
        </Box>
      ) : (
        <>
          {root && (
            <QTree
              qid={root.id} questions={questions} depth={0}
              onEdit={qid => setModal({ type: 'edit', qid })}
              onDelete={handleDelete}
              onAddChild={handleAddChild}
              onManageAnswers={handleManageAnswers}
              onAssignFollowup={handleAssignFollowup}
              visited={new Set()}
            />
          )}

          {/* Orphan / unlinked questions */}
          {orphans.length > 0 && (
            <Box mt={3}>
              <Box sx={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#dc2626', opacity: 0.85, mb: 1 }}>
                ⚠ Unlinked Questions ({orphans.length})
              </Box>
              {orphans.map(q => (
                <QTree
                  key={q.id} qid={q.id} questions={questions} depth={0}
                  onEdit={qid => setModal({ type: 'edit', qid })}
                  onDelete={handleDelete}
                  onAddChild={handleAddChild}
                  onManageAnswers={handleManageAnswers}
                  onAssignFollowup={handleAssignFollowup}
                  visited={new Set()}
                />
              ))}
            </Box>
          )}

          {/* Legend */}
          <Box mt={2.5} pt={1.5} sx={{ borderTop: '1px solid', borderColor: 'divider' }}>
            <MDTypography variant="body2" color="text.secondary">
              ▶ Click a row to expand &nbsp;·&nbsp; + Follow-up Question links an answer to a new question &nbsp;·&nbsp; ⠿ Drag an option that has a follow-up onto another option of the same question to copy it (independent copy) &nbsp;·&nbsp; Manage opens the full answer editor (features, meta, active status)
            </MDTypography>
          </Box>
        </>
      )}

      {/* QuestionModal */}
      {modal && (
        <QuestionModal
          q={modal.type === 'edit' ? questions[modal.qid] : null}
          questionnaireId={questionnaireId}
          allQuestions={questions}
          onSave={handleModalSave}
          onClose={() => setModal(null)}
          isChild={modal.type === 'child'}
          isFirstQuestion={modal.type === 'new' && catQs.length === 0}
          questionsApi={questionsApi}
          answerOptionsApi={answerOptionsApi}
        />
      )}

      {/* AnswerOptionsModal — full feature/meta editor (unchanged) */}
      {answerModal && (
        <AnswerOptionsModal
          open={!!answerModal}
          onClose={() => { setAnswerModal(null); loadAll(); }}
          question={answerModal.question}
          questions={answerModal.questions}
        />
      )}

      {/* Delete confirmation modal */}
      <DeleteConfirmModal
        data={deleteConfirm}
        onConfirm={handleDeleteConfirmed}
        onCancel={() => setDeleteConfirm(null)}
      />

      {/* Import / copy a question from another questionnaire (page) */}
      {importModal && (
        <ImportQuestionModal
          currentQuestionnaireId={questionnaireId}
          questionnaires={questionnaires}
          currentQuestions={questions}
          fetchQuestionsMap={fetchQuestionsMap}
          onImport={handleImportQuestion}
          onClose={() => setImportModal(false)}
        />
      )}

      {/* Undo bar — reverse the last add (delete the created question + restore link) */}
      <Snackbar
        open={!!undo}
        autoHideDuration={10000}
        onClose={(e, reason) => { if (reason !== 'clickaway') setUndo(null); }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        message={undo?.label || 'Added'}
        action={
          <MuiButton
            size="small"
            onClick={runUndo}
            disabled={undo?.busy}
            sx={{ color: '#fff', fontWeight: 700 }}
          >
            {undo?.busy ? 'Undoing…' : 'Undo'}
          </MuiButton>
        }
      />
    </>
  );
}

// ─────────────────────────────────────────────────────────
//  QuestionsPage — ERP layout wrapper
// ─────────────────────────────────────────────────────────
function QuestionsPage() {
  const canRead = usePermission('qa-center', 'read');
  const { questionnairesApi, questionsApi, answerOptionsApi } = useServicePlanApi();

  const [questionnaires,          setQuestionnaires]          = useState([]);
  const [selectedQuestionnaireId, setSelectedQuestionnaireId] = useState('');
  const [loadingQuestionnaires,   setLoadingQuestionnaires]   = useState(false);

  useEffect(() => {
    if (!canRead) {
      setLoadingQuestionnaires(false);
      return;
    }
    fetchQuestionnaires();
  }, [canRead]);

  const fetchQuestionnaires = async () => {
    setLoadingQuestionnaires(true);
    try {
      const res = await questionnairesApi.getAll();
      const all = res.data.data || res.data || [];
      setQuestionnaires(all);
      const first = all.find(q => q.isActive);
      if (first) setSelectedQuestionnaireId(first.id);
    } catch {
      Notify.failure('Failed to load questionnaires');
    } finally {
      setLoadingQuestionnaires(false);
    }
  };

  const selectedQnaire = questionnaires.find(q => q.id === selectedQuestionnaireId);

  if (!canRead) {
    return (
      <MDBox>
        <NoPermissionNotice message="Your role doesn't include read access to Questions. Ask an administrator to update your permissions." />
      </MDBox>
    );
  }

  return (
    <MDBox>
      {/* Questionnaire selector */}
      <Card sx={{ mb: 2.5, px: 2.5, py: 1.8, border: '1.5px solid', borderColor: 'divider', borderRadius: 2.5, boxShadow: 'none' }}>
        <Box display="flex" alignItems="center" gap={3} flexWrap="wrap">
          <FormControl size="small" sx={{ minWidth: 280 }}>
            <InputLabel>Select Questionnaire</InputLabel>
            <MuiSelect
              value={selectedQuestionnaireId}
              onChange={e => setSelectedQuestionnaireId(e.target.value)}
              label="Select Questionnaire"
              disabled={loadingQuestionnaires}
            >
              {questionnaires.map(q => (
                <MenuItem key={q.id} value={q.id}>
                  <Box>
                    <Typography sx={{ fontSize: 14, fontWeight: 600, lineHeight: 1.3 }}>{q.name}</Typography>
                    <Typography sx={{ fontSize: 12, color: 'text.secondary', lineHeight: 1.3 }}>
                      {q.page?.name}{q.page?.route ? ` · ${q.page.route}` : ''}
                    </Typography>
                  </Box>
                </MenuItem>
              ))}
            </MuiSelect>
          </FormControl>

          {loadingQuestionnaires && <CircularProgress size={18} sx={{ color: ACCENT }} />}

          {selectedQnaire && (
            <>
              <Divider orientation="vertical" flexItem sx={{ height: 32 }} />
              <Box>
                <Typography sx={{ fontSize: 13, color: 'text.secondary', lineHeight: 1.5 }}>
                  Linked to: <strong style={{ color: '#111827' }}>{selectedQnaire.page?.name}</strong>
                </Typography>
                <Typography sx={{ fontSize: 13, color: 'text.secondary', lineHeight: 1.5 }}>
                  Route: {selectedQnaire.page?.route}
                </Typography>
              </Box>
            </>
          )}
        </Box>
      </Card>

      {/* Content */}
      {!selectedQuestionnaireId ? (
        <Card sx={{ p: 6, textAlign: 'center', border: '1.5px dashed', borderColor: 'divider', boxShadow: 'none', borderRadius: 3 }}>
          <QuizOutlined sx={{ fontSize: 48, color: 'text.disabled', mb: 1.5 }} />
          <MDTypography variant="body2" color="text.secondary">
            Select a questionnaire above to view or manage its questions.
          </MDTypography>
        </Card>
      ) : (
        <Card sx={{ p: 2.5, border: '1.5px solid', borderColor: 'divider', borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
          <QuestionsContent
            key={selectedQuestionnaireId}
            questionnaireId={selectedQuestionnaireId}
            questionnaires={questionnaires}
            questionsApi={questionsApi}
            answerOptionsApi={answerOptionsApi}
          />
        </Card>
      )}
    </MDBox>
  );
}

export default QuestionsPage;
